import React, { FC, useEffect, useMemo, useState } from 'react'

import styled from 'styled-components'

import { useCheckSpotifyCheckoutStepIsAllowed } from 'src/Pages/Artist/SpotifySchedulePage/helpers/useCheckSpotifyCheckoutStepIsAllowed'
import { usePaymentCancelledSpotifyEvents } from 'src/Hooks/usePaymentCancelledSpotifyEvents'
import { SchedulePaymentMethod } from 'src/Components/SchedulePaymentMethod'
import { ScheduleSummary } from 'src/Components/ScheduleSummary'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { getIsProcessingPage } from 'src/Redux/paypalTransactions-process/selectors'
import {
  getPaymentAlertError,
  getPaymentMethod,
} from 'src/Redux/payment-process/selectors'
import { setPaymentMethod } from 'src/Redux/payment-process'
import { ScheduleCheckoutErrors } from 'src/Components/ScheduleCheckoutErrors'
import { SchedulePaymentProcessingModal } from 'src/Components/SchedulePaymentProcessingModal'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { useWalletPaymentMethods } from 'src/Hooks/useWalletPaymentMethods'
import {
  getDisplayedPaymentMethods,
  getIsConfirmDisabled,
} from 'src/Utils/displayedPaymentMethods'

import { useSpotifySchedulePaymentMethod } from './useSpotifySchedulePaymentMethod'
import { useSpotifyScheduleOrderSummary } from './useSpotifyScheduleOrderSummary'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media screen and (min-width: 768px) {
    align-items: flex-start;
    flex-direction: row;
    align-self: stretch;
  }
`

export const SpotifyScheduleOrderCheckoutPage: FC = () => {
  useCheckSpotifyCheckoutStepIsAllowed()
  usePaymentCancelledSpotifyEvents()
  const dispatch = useAppDispatch()
  const spotifySchedulePaymentMethodProps = useSpotifySchedulePaymentMethod()
  const {
    availablePaymentMethods,
    isOrderFullyCovered,
    isSyncing,
    isWalletPaymentEligible,
    walletAmount,
    onConfirmWalletPayment,
    onWalletPaymentSuccess,
    onWalletPaymentError,
    ...spotifyScheduleSummaryProps
  } = useSpotifyScheduleOrderSummary()

  const paymentError = useAppSelector(getPaymentAlertError)
  const isProcessingPageVisible = useAppSelector(getIsProcessingPage)
  const paymentMethod = useAppSelector(getPaymentMethod)
  const { campaignDetails, isPreRelease } = useSpotifyCampaignStatus()
  const [isWalletProcessing, setIsWalletProcessing] = useState(false)

  const { paypalEmail } = spotifySchedulePaymentMethodProps

  const isWalletExpected = isWalletPaymentEligible && walletAmount > 0

  const {
    walletPaymentMethods,
    isWalletInitializing,
    onWalletAvailabilityChange,
    onChangePaymentMethod,
  } = useWalletPaymentMethods({
    onChange: spotifySchedulePaymentMethodProps.onChange,
    canSelectWalletByDefault: !paypalEmail,
    isWalletExpected,
  })

  const displayedPaymentMethods = useMemo(
    () =>
      getDisplayedPaymentMethods({
        availablePaymentMethods,
        walletPaymentMethods,
        isOrderFullyCovered,
        paypalEmail,
      }),
    [
      availablePaymentMethods,
      walletPaymentMethods,
      isOrderFullyCovered,
      paypalEmail,
    ],
  )

  useEffect(() => {
    if (
      displayedPaymentMethods?.length &&
      !displayedPaymentMethods.includes(paymentMethod)
    ) {
      dispatch(setPaymentMethod(displayedPaymentMethods[0]))
    }
  }, [displayedPaymentMethods, paymentMethod, dispatch])

  const isConfirmDisabled = getIsConfirmDisabled(
    isWalletProcessing,
    paymentMethod,
    displayedPaymentMethods,
  )

  const isCheckoutLoading = !availablePaymentMethods || isWalletInitializing

  const paymentMethodBlock = (
    <SchedulePaymentMethod
      {...spotifySchedulePaymentMethodProps}
      isEnoughBalance={isOrderFullyCovered}
      onChange={onChangePaymentMethod}
      allowedPaymentMethods={displayedPaymentMethods}
      walletPaymentMethods={walletPaymentMethods}
      isLoading={isCheckoutLoading}
    />
  )

  const walletCheckout = isWalletExpected
    ? {
        amount: walletAmount,
        onWalletProcessingChange: setIsWalletProcessing,
        onConfirmWalletPayment,
        onWalletPaymentSuccess,
        onWalletPaymentError,
        onWalletAvailabilityChange,
      }
    : void 0

  return (
    <>
      {isProcessingPageVisible && <SchedulePaymentProcessingModal />}
      {paymentError && <ScheduleCheckoutErrors name={paymentError} />}
      <Wrapper>
        {paymentMethodBlock}
        <ScheduleSummary
          {...spotifyScheduleSummaryProps}
          confirmDisabled={isConfirmDisabled || isSyncing}
          paymentMethod={paymentMethod}
          trackCover={campaignDetails?.track.cover}
          isLoading={isCheckoutLoading}
          walletCheckout={walletCheckout}
          isPreRelease={isPreRelease}
        />
      </Wrapper>
    </>
  )
}
